import { type ListStatusFilter, type DateSort, type PriceSort, peso } from "./model";
import { type Order } from "../../lib/api";
import { BriefcaseBusiness, ShoppingBag, Search, SlidersHorizontal, ChevronRight } from "lucide-react";

import { Tabs, TabsList, TabsTrigger } from "../../components/beui/Tabs";
import { Card } from "../../components/beui/SurfaceCard";
import { Input } from "../../components/beui/Input";
import { Button } from "../../components/beui/Button";
import { SharedLayoutBg } from "../../components/motion/shared-layout-bg";
import { isNativeApp } from "../../lib/platform";
import { useEffect } from "react";

export type ContractListRow = {
  id: string;
  title: string;
  amount: number | null;
  valueLabel?: string;
  createdAt: string;
  role: string;
  counterparty?: string | null;
  sourceLabel: string;
  statusKey: string;
  statusLabel: string;
  statusClass: string;
  href: string;
};

type ContractDesktopListProps = {
  listType: "purchase" | "work";
  navigate: (href: string) => void;
  contractsLoaded: boolean;
  workCount: number;
  purchasesLoaded: boolean;
  orders: Order[];
  statusFilters: ReadonlyArray<{ key: ListStatusFilter; label: string }>;
  activeFilter: ListStatusFilter;
  setPurchaseStatusFilter: (filter: ListStatusFilter) => void;
  setStatusFilter: (filter: ListStatusFilter) => void;
  t: (key: string) => string;
  listLoaded: boolean;
  statusCount: (status: string) => number;
  search: string;
  setSearch: (value: string) => void;
  dateSort: DateSort;
  setDateSort: (value: DateSort) => void;
  priceSort: PriceSort;
  setPriceSort: (value: PriceSort) => void;
  listError: string | null;
  load: () => Promise<void>;
  visibleRows: ContractListRow[];
  listRows: ContractListRow[];
  openAgreement: (href: string) => void;
  onOverlayChange: (open: boolean) => void;
};

export function ContractDesktopList({ listType, navigate, contractsLoaded, workCount, purchasesLoaded, orders, statusFilters,
  activeFilter, setPurchaseStatusFilter, setStatusFilter, t, listLoaded, statusCount, search, setSearch, dateSort,
  setDateSort, priceSort, setPriceSort, listError, load, visibleRows, listRows, openAgreement, onOverlayChange }: ContractDesktopListProps) {
  // Detail navigation and responsive projection changes unmount this dropdown;
  // an absent overlay must not leave its parent refresh boundary disabled.
  useEffect(() => () => onOverlayChange(false), [onOverlayChange]);
  return (
    <div className="container-x py-6 pb-24 max-w-5xl">
      <h1 className="mb-4 text-2xl font-extrabold text-navy">Contracts</h1>
      <Card className="gap-0 overflow-visible rounded-2xl py-0 shadow-sm">
          <Tabs value={listType} onValueChange={(value) => navigate(value === "purchase" ? "/contracts?type=purchase" : "/contracts")} variant="underline" className="border-b border-line px-3 pt-3">
            <TabsList aria-label="Agreement types" className="border-0">
              <TabsTrigger value="work" icon={<BriefcaseBusiness className="h-4 w-4" />}>Work <span className="ml-2 text-xs tabular-nums text-mut">{contractsLoaded ? workCount : "—"}</span></TabsTrigger>
              <TabsTrigger value="purchase" icon={<ShoppingBag className="h-4 w-4" />}>Purchases <span className="ml-2 text-xs tabular-nums text-mut">{purchasesLoaded ? orders.length : "—"}</span></TabsTrigger>
            </TabsList>
          </Tabs>
          <div aria-label="Status filters" className="flex flex-wrap gap-2 border-b border-line p-4">
            {statusFilters.map(({ key, label }) => (
              <Button variant={activeFilter === key ? "primary" : "ghost"} pressScale={0.98} key={key} aria-pressed={activeFilter === key} onClick={() => listType === "purchase" ? setPurchaseStatusFilter(key) : setStatusFilter(key)} className="h-9 shrink-0 rounded-full px-3 text-xs font-semibold">
                {key === "completed" || key === "cancelled" ? t(`transaction.status.${key}`) : label.startsWith("contracts.filter.") ? t(label) : label} <span className="ml-1">{listLoaded ? statusCount(key) : "—"}</span>
              </Button>
            ))}
          </div>
          <div className="flex gap-2 border-b border-line p-4">
            <Input aria-label={listType === "purchase" ? "Search purchases" : "Search work"} value={search} onChange={setSearch} className="min-w-0 flex-1" leftIcon={<Search className="h-4 w-4" />} placeholder={listType === "purchase" ? "Search purchases or sales" : t("contracts.search")} />
            <details className="group relative" onToggle={event => onOverlayChange(event.currentTarget.open)}>
              <summary className="flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-lg border border-line text-navy hover:bg-navy-50" aria-label="Sort contracts">
                <SlidersHorizontal className="h-4 w-4" />
              </summary>
              <div className="absolute right-0 z-20 mt-2 w-60 rounded-xl border border-line bg-white p-2 shadow-xl">
                <SortGroup label="Date created" priority={1} options={[["newest", "Newest first"], ["oldest", "Oldest first"]]} value={dateSort} onChange={(value) => setDateSort(value as DateSort)} />
                <div className="mt-2 border-t border-line pt-2">
                  <SortGroup label={listType === "purchase" ? "Purchase total" : "Contract price"} priority={2} options={[["highest", "Highest first"], ["lowest", "Lowest first"]]} value={priceSort} onChange={(value) => setPriceSort(value as PriceSort)} />
                </div>
              </div>
            </details>
          </div>
          {listError && <div role="alert" className="flex items-center justify-between gap-3 border-b border-line p-4 text-sm text-brand-red"><span>{listError}</span>{!isNativeApp() && <button onClick={load} disabled={!listLoaded} className="btn-outline h-9 shrink-0 px-3 text-sm">Retry</button>}</div>}
          <div className="px-4">
            {!listLoaded && <div role="status" className="p-10 text-center text-sm text-mut">Loading {listType === "purchase" ? "purchases" : "work"}…</div>}
            {listLoaded && !listError && visibleRows.length === 0 && <div className="p-10 text-center text-sm text-mut">{listRows.length === 0 ? listType === "purchase" ? "No purchases or sales yet." : "No work yet." : listType === "purchase" ? "No purchases or sales match this filter." : "No work matches this filter."}</div>}
            <SharedLayoutBg inset={0} pillClassName="bg-navy-50" className="divide-y divide-line">
            {listLoaded && visibleRows.map((row) => (
                <div key={row.id}><button onClick={() => openAgreement(row.href)} className={`relative grid w-full items-center gap-4 px-1 py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/50 ${activeFilter === "all" ? "md:grid-cols-[minmax(0,1fr)_12rem_12rem_4rem_1.5rem]" : "md:grid-cols-[minmax(0,1fr)_12rem_4rem_1.5rem]"}`}>
                  <div className="relative min-w-0">
                    <div className="truncate font-bold text-navy">{row.title}</div>
                    <div className="mt-1 text-xs text-mut"><span className="chip mr-1 bg-navy-50 text-navy-600 capitalize">{row.role}</span> · {row.counterparty || "—"} · {row.sourceLabel}</div>
                  </div>
                  {activeFilter === "all" && <span className={`chip w-max ${row.statusClass}`}>{row.statusLabel}</span>}
                  <span className="min-w-0 [overflow-wrap:anywhere] font-extrabold text-navy md:text-base">{row.valueLabel ?? (row.amount == null ? "—" : peso(row.amount))}</span>
                  <span className="hidden text-xs text-mut md:block">{new Date(row.createdAt).toLocaleDateString("en-PH", { month: "short", day: "numeric" })}</span>
                  <ChevronRight className="hidden h-4 w-4 text-brand-red md:block" />
                </button></div>
            ))}
            </SharedLayoutBg>
          </div>
      </Card>
    </div>
  );
}

function SortGroup({ label, priority, options, value, onChange }: { label: string; priority: number; options: Array<[string, string]>; value: string | null; onChange: (value: string | null) => void }) {
  return <div><div className="flex justify-between px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-mut"><span>{label}</span><span>Priority {priority}</span></div>{options.map(([option, text]) => <button key={option} onClick={() => onChange(value === option ? null : option)} className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ${value === option ? "bg-navy-50 font-semibold text-navy" : "text-navy-700 hover:bg-navy-50/60"}`}>{value === option && <span className="grid h-5 w-5 place-items-center rounded-full bg-navy text-[10px] text-white">{priority}</span>}{text}</button>)}</div>;
}
